import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  Paper, Stack, Grid, CircularProgress, Card, InputAdornment, MenuItem
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';
import SearchIcon from '@mui/icons-material/Search';

const Bankaccounts = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    className: '',
    ifsccode: '',
    accountno: '',
    bankid: ''
  });
  const [banks, setBanks] = useState([]);
  const [bankaccounts, setBankaccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [schoolId, setSchoolId] = useState(null);

  // Fetch schoolId from the logged-in user
  const fetchSchoolId = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser?.uid));
      if (userDoc.exists()) {
        setSchoolId(userDoc.data().schoolId);
      } else {
        toast.error("User data not found");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      toast.error("Error fetching user data");
    }
  };

  // Fetch bank accounts for this school
  const fetchBankaccounts = async (schoolId) => {
    if (!schoolId) return;
    try {
      const q = query(collection(db, 'bankaccounts'), where('schoolId', '==', schoolId));
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBankaccounts(data);
    } catch (err) {
      console.error("Error fetching bank accounts:", err);
    }
  };

  // Fetch banks for this school
  const fetchBanks = async (schoolId) => {
    if (!schoolId) return;
    try {
      const q = query(collection(db, 'banks'), where('schoolId', '==', schoolId));
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBanks(data);
    } catch (err) {
      console.error("Error fetching banks:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchSchoolId();
    };
    init();
  }, []);

  useEffect(() => {
    if (schoolId) {
      fetchBankaccounts(schoolId);
      fetchBanks(schoolId);
    }
  }, [schoolId]);

  const handleOpen = () => {
    setFormData({ className: '', ifsccode: '', accountno: '', bankid: '' });
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const { className, ifsccode, accountno, bankid } = formData;

    if (!className || !ifsccode || !accountno || !bankid) {
      toast.error("All fields are required");
      return;
    }

    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }

    setLoading(true);

    const data = { className, ifsccode, accountno, bankid, schoolId };

    try {
      if (editMode && editId) {
        await updateDoc(doc(db, 'bankaccounts', editId), data);
        toast.success("Bank Account updated");
      } else {
        await addDoc(collection(db, 'bankaccounts'), data);
        toast.success("Bank Account added");
      }

      fetchBankaccounts(schoolId);
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Error: " + err.message);
    }

    setLoading(false);
  };

  const handleEdit = (exam) => {
    setFormData({
      className: exam.className || '',
      ifsccode: exam.ifsccode || '',
      accountno: exam.accountno || '',
      bankid: exam.bankid || ''
    });
    setEditId(exam.id);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'bankaccounts', id));
      toast.success("Bank Account deleted");
      fetchBankaccounts(schoolId);
    } catch (err) {
      toast.error("Error deleting bank account");
    }
  };

  const filtered = bankaccounts.filter(exam =>
    exam.className.toLowerCase().includes(searchTerm)
  );

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button variant="contained" onClick={handleOpen}>Add Bank Account</Button>
        <Typography variant="h5" fontWeight="bold">Bank Accounts</Typography>
      </Stack>

      <Card>
        <Box sx={{ p: 1 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search.."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            fullWidth
          />
        </Box>

        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                <TableCell>#</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>IFSC</TableCell>
                <TableCell>Account No</TableCell>
                <TableCell>Bank</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No records found</TableCell>
                </TableRow>
              ) : (
                paginated.map((exam, index) => {
                  const bank = banks.find(b => b.id === exam.bankid);
                  return (
                    <TableRow key={exam.id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" onClick={() => handleEdit(exam)}>Edit</Button>
                          <Button size="small" variant="contained" color="error" onClick={() => handleDelete(exam.id)}>Delete</Button>
                        </Stack>
                      </TableCell>
                      <TableCell>{exam.className}</TableCell>
                      <TableCell>{exam.ifsccode}</TableCell>
                      <TableCell>{exam.accountno}</TableCell>
                      <TableCell>{bank ? bank.bankname : 'N/A'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Bank Account</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField label="Bank Account Name" name="className" value={formData.className} onChange={handleChange} fullWidth size="small" required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="IFSC" name="ifsccode" value={formData.ifsccode} onChange={handleChange} fullWidth size="small" required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Account Number" name="accountno" value={formData.accountno} onChange={handleChange} fullWidth size="small" required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Select Bank"
                name="bankid"
                value={formData.bankid}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              >
                {banks.map(bank => (
                  <MenuItem key={bank.id} value={bank.id}>
                    {bank.bankname}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bankaccounts;